<template>
  <v-dialog v-model="dialog" max-width="600" persistent :scrim="true">
    <v-card>
      <v-card-title class="d-flex align-center pa-4" :style="headerStyle">
        <v-icon left color="white" class="mr-2">{{
          importanceConfig.icon
        }}</v-icon>
        <span>{{ importanceConfig.title }}</span>
        <v-spacer></v-spacer>
        <v-btn icon small @click="skipNews" color="white">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="pa-6">
        <div class="news-content">
          <h3 class="mb-3" style="color: #3b4573">
            {{ currentMessage.title }}
          </h3>
          <p class="mb-4" style="color: #1e1e24">
            {{ currentMessage.description }}
          </p>

          <v-list density="compact">
            <v-list-item
              v-for="(item, index) in currentMessage.items"
              :key="index"
              class="mb-2"
            >
              <template v-slot:prepend>
                <v-icon color="#76BA70">mdi-check-circle</v-icon>
              </template>
              <v-list-item-title class="text-wrap" style="color: #1e1e24; white-space: normal !important; word-break: break-word;">{{
                item
              }}</v-list-item-title>
            </v-list-item>
          </v-list>
        </div>
      </v-card-text>

      <v-card-actions class="px-6 pb-4">
        <v-btn variant="outlined" color="#64789D" @click="skipNews">
          Skip
        </v-btn>
        <v-spacer></v-spacer>
        <v-btn
          :color="importanceConfig.color"
          variant="elevated"
          @click="handleAction"
        >
          {{ importanceConfig.actionText }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
const STORAGE_KEY = "t2d_news_last_shown";
import { mapState } from "vuex";
export default {
  name: "NewsPopup",
  data() {
    return {
      dialog: false,
      hasPendingFaqs: false,
      hasPendingOrders: false,
      pendingOrderDetails: {
        totalCount: 0,
        orderNames: []
      },
      faqApi: {
        isLoading: false,
        isError: false,
        error: null,
        url: null,
      },
      ordersApi: {
        isLoading: false,
        isError: false,
        error: null,
        url: null,
      },
      // Current message key: 'updateFaqs' or 'markCompleted'
      currentMessageKey: "markCompleted",
      newsItems: [
        "Review and update outdated FAQ responses",
        "Add answers to newly submitted customer questions",
        "Verify technical accuracy of all answers",
        "Ensure consistency across all FAQ categories",
      ],
      // Messages for different scenarios
      messages: {
        updateFaqs: {
          importance: "critical",
          title: "Critical Alert – Action Required Immediately",
          description:
            "Your FAQ answers are overdue for review.",
          items: [
            "All technician FAQs must be reviewed every 3 months",
            "If nothing has changed, you must still re-save the answer to confirm accuracy",
            "Please review and update all assigned FAQs now",
            "This action is mandatory",
          ],
        },
        markCompleted: {
          importance: "task",
          title: "Action Required – Immediate Attention Needed",
          description:
            "The following completed orders remain open. Please review and close them immediately:",
          items: [
            "Review these orders",
            "Choose the appropriate Customer review option",
            "Close them immediately",
            "This is essential for maintaining a high-quality customer experience and generating future business",
          ],
        },
      },
    };
  },
  computed: {
    currentMessage() {
      // Change currentMessageKey in data() based on your condition
      const baseMessage = this.messages[this.currentMessageKey] || this.messages.updateFaqs;
      
      // If it's the markCompleted message and we have order details, customize items
      if (this.currentMessageKey === 'markCompleted' && this.pendingOrderDetails.orderNames.length > 0) {
        const items = [...this.pendingOrderDetails.orderNames];
        const remaining = this.pendingOrderDetails.totalCount - this.pendingOrderDetails.orderNames.length;
        
        if (remaining > 0) {
          items.push(`+ ${remaining} more order${remaining > 1 ? 's' : ''}`);
        }
        
        // Add the instructional messages after the order names
        items.push('');
        items.push('Review these orders and choose the appropriate Customer review option');
        items.push('Close them immediately');
        items.push('This is essential for maintaining a high-quality customer experience and generating future business');
        
        return {
          ...baseMessage,
          items: items
        };
      }
      
      return baseMessage;
    },
    importanceConfig() {
      const configs = {
        info: {
          color: "#64789D",
          icon: "mdi-information",
          title: "Latest Updates",
          actionText: "Learn More",
        },
        warning: {
          color: "#FFC107",
          icon: "mdi-alert",
          title: "Important Notice",
          actionText: "Review Now",
        },
        critical: {
          color: "#FF5252",
          icon: "mdi-alert-circle",
          title: "Critical Alert",
          actionText: "Update FAQs",
        },
        success: {
          color: "#76BA70",
          icon: "mdi-check-circle",
          title: "Great News!",
          actionText: "View Details",
        },
        task: {
          color: "#FFC107", // add warning colour
          icon: "mdi-clipboard-check",
          title: "Action Required",
          actionText: "View Orders",
        },
        default: {
          color: "#3B4573",
          icon: "mdi-newspaper-variant",
          title: "Latest News & Updates",
          actionText: "Learn More",
        },
      };
      const importance = this.currentMessage.importance || "default";
      return configs[importance] || configs.default;
    },
    headerStyle() {
      return {
        backgroundColor: this.importanceConfig.color,
        color: "white",
      };
    },
     ...mapState({
      account: (state) => state.account.data,
    }),
  },
  mounted() {
    // Only check for non-admin users
    if (!this.account.type || !this.account.type.includes('admin')) {
      this.checkPendingFAQs();
      this.checkPendingOrders();
    }
  },
  methods: {
    checkPendingFAQs() {
      this.faqApi.method = "GET";
      this.faqApi.url = `${this.$api.servers.backend}/api/v1/technician-faqs/check-pending`;
      this.faqApi.callbackReset = () => {
        this.faqApi.isLoading = true;
        this.faqApi.isError = false;
        this.faqApi.error = null;
      };
      this.faqApi.callbackError = (e) => {
        this.faqApi.isLoading = false;
        this.faqApi.isError = true;
        this.faqApi.error = e;
        this.checkPopupConditions();
      };
      this.faqApi.callbackSuccess = (resp) => {
        this.faqApi.isLoading = false;
        this.hasPendingFaqs = resp.hasPending;
        this.checkPopupConditions();
      };

      this.$api.fetch(this.faqApi);
    },
    checkPendingOrders() {
      this.ordersApi.method = "GET";
      this.ordersApi.url = `${this.$api.servers.backend}/api/v2/orders/check-pending`;
      this.ordersApi.callbackReset = () => {
        this.ordersApi.isLoading = true;
        this.ordersApi.isError = false;
        this.ordersApi.error = null;
      };
      this.ordersApi.callbackError = (e) => {
        this.ordersApi.isLoading = false;
        this.ordersApi.isError = true;
        this.ordersApi.error = e;
        this.checkPopupConditions();
      };
      this.ordersApi.callbackSuccess = (resp) => {
        this.ordersApi.isLoading = false;
        this.hasPendingOrders = resp.hasPending;
        if (resp.hasPending) {
          this.pendingOrderDetails = {
            totalCount: resp.totalCount || 0,
            orderNames: resp.orderNames || []
          };
        }
        this.checkPopupConditions();
      };

      this.$api.fetch(this.ordersApi);
    },
    checkPopupConditions() {
      // Wait for both API calls to complete
      if (this.faqApi.isLoading || this.ordersApi.isLoading) {
        return;
      }

      // Determine which message to show based on priority
      // Priority: FAQs > Orders
      if (this.hasPendingFaqs) {
        this.currentMessageKey = 'updateFaqs';
        this.checkAndShowPopup();
      } else if (this.hasPendingOrders) {
        this.currentMessageKey = 'markCompleted';
        this.checkAndShowPopup();
      }
      // If neither has pending items, don't show popup
    },
    checkAndShowPopup() {
      const lastShown = localStorage.getItem(STORAGE_KEY);
      const now = new Date().getTime();

      if (!lastShown) {
        // First time - show popup
        this.showPopup();
      } else {
        const lastShownDate = new Date(parseInt(lastShown));
        const currentDate = new Date();

        // Check if it's a different day
        const isDifferentDay =
          lastShownDate.getDate() !== currentDate.getDate() ||
          lastShownDate.getMonth() !== currentDate.getMonth() ||
          lastShownDate.getFullYear() !== currentDate.getFullYear();

        if (isDifferentDay) {
          this.showPopup();
        }
      }
    },
    showPopup() {
      // Small delay for better UX
      setTimeout(() => {
        this.dialog = true;
      }, 500);
    },
    skipNews() {
      this.dialog = false;
      this.saveLastShownTime();
    },
    handleAction() {
      // You can customize this action - for example, navigate to a news page
      // or open a specific route
      this.dialog = false;
      this.saveLastShownTime();
      if(this.currentMessageKey === 'updateFaqs') {
        this.$router.push({ name: 'TechnicianFAQBrowseView' });
      } else if(this.currentMessageKey === 'markCompleted') {
        this.$router.push({ name: 'OrderBrowseView', query: { status: 'posted' } });
      }

      // Example: Navigate to a specific route or open external link
      // this.$router.push({ name: 'NewsPage' });
      // window.open('https://your-news-page.com', '_blank');

      console.log("User clicked Learn More");
    },
    saveLastShownTime() {
      const now = new Date().getTime();
      localStorage.setItem(STORAGE_KEY, now.toString());
    },
  },
};
</script>

<style scoped>
.news-content h3 {
  font-weight: 600;
}

.news-content p {
  line-height: 1.6;
}

.v-list-item {
  padding-left: 0;
}

.v-list-item-title {
  white-space: normal !important;
  word-wrap: break-word !important;
  word-break: break-word !important;
  overflow-wrap: break-word !important;
  line-height: 1.5;
  max-width: 100%;
}

.text-wrap {
  white-space: normal !important;
  overflow: visible !important;
  text-overflow: clip !important;
}
</style>
